import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ApiError, api } from './api/client';
import type { Capabilities, User } from './api/types';
import { IconCalls, IconCoaching, IconDashboard, IconRubric, IconUpload, Skeleton } from './components/ui';
import { CallAuditPage } from './pages/CallAudit';
import { CallsPage } from './pages/Calls';
import { CoachingPointsPage } from './pages/CoachingPoints';
import { DashboardPage } from './pages/Dashboard';
import { LoginPage } from './pages/Login';
import { NewCallPage } from './pages/NewCall';
import { RubricPage } from './pages/Rubric';

interface Session {
  user: User | null;
  capabilities: Capabilities | null;
  loading: boolean;
  signIn: (u: User) => void;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Session>({
  user: null,
  capabilities: null,
  loading: true,
  signIn: () => {},
  signOut: async () => {},
});

export const useSession = () => useContext(SessionContext);

function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [capabilities, setCapabilities] = useState<Capabilities | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    api
      .me()
      .then(({ user: u }) => {
        if (live) setUser(u);
      })
      .catch((e: unknown) => {
        // A 401 here is the normal signed-out case, not an error worth showing.
        if (live && !(e instanceof ApiError && e.status === 401)) console.error(e);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setCapabilities(null);
      return;
    }
    let live = true;
    api
      .capabilities()
      .then((c) => {
        if (live) setCapabilities(c);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [user]);

  const signIn = useCallback((u: User) => setUser(u), []);
  const signOut = useCallback(async () => {
    await api.logout().catch(() => {});
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, capabilities, loading, signIn, signOut }), [user, capabilities, loading, signIn, signOut]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

function Sidebar() {
  const { user, capabilities, signOut } = useSession();
  if (!user) return null;

  const isSdr = user.role === 'sdr';
  const initials = user.name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  return (
    <nav className="sidebar" aria-label="Main">
      <div className="brand">
        <div className="brand-mark" aria-hidden>
          SQ
        </div>
        <div className="brand-text">
          <div className="brand-name">SDR Call QA</div>
          <div className="brand-sub">Streamlinerz</div>
        </div>
      </div>

      <NavLink to="/" end className="nav-item">
        <IconDashboard />
        <span>{isSdr ? 'My performance' : 'Dashboard'}</span>
      </NavLink>
      <NavLink to="/calls" end className="nav-item">
        <IconCalls />
        <span>{isSdr ? 'My calls' : 'Calls'}</span>
      </NavLink>
      {!isSdr && (
        <NavLink to="/calls/new" className="nav-item">
          <IconUpload />
          <span>Submit a call</span>
        </NavLink>
      )}
      <NavLink to="/rubric" className="nav-item">
        <IconRubric />
        <span>The standard</span>
      </NavLink>
      <NavLink to="/coaching" className="nav-item">
        <IconCoaching />
        <span>{isSdr ? 'My coaching points' : 'Coaching points'}</span>
      </NavLink>

      {capabilities && !capabilities.aiIsLanguageModel && (
        <div className="nav-label" style={{ paddingTop: 18 }} title="Audits on this deployment are produced by a local rule engine, not a language model.">
          Local analysis mode
        </div>
      )}

      <div className="sidebar-footer">
        <div className="user-chip">
          <div className="avatar" aria-hidden>
            {initials}
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="user-name">{user.name}</div>
            <div className="user-role">{user.role}</div>
          </div>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" style={{ width: '100%' }} onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    </nav>
  );
}

function Protected({ children }: { children: ReactNode }) {
  const { user, loading } = useSession();
  const location = useLocation();
  if (loading) {
    return (
      <div style={{ padding: 32, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 600 }}>
        <Skeleton height={26} width={220} />
        <Skeleton height={80} />
        <Skeleton height={80} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return <>{children}</>;
}

function Shell() {
  return (
    <div className="shell">
      {/* Keyboard users should not have to tab the whole sidebar to reach the page. */}
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <Sidebar />
      <main className="main" id="main">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/calls" element={<CallsPage />} />
          <Route path="/calls/new" element={<NewCallPage />} />
          <Route path="/calls/:id" element={<CallAuditPage />} />
          <Route path="/rubric" element={<RubricPage />} />
          <Route path="/coaching" element={<CoachingPointsPage />} />
          <Route path="/coaching/:sdrId" element={<CoachingPointsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <SessionProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="*"
          element={
            <Protected>
              <Shell />
            </Protected>
          }
        />
      </Routes>
    </SessionProvider>
  );
}
